import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, createSearchParams } from 'react-router-dom';
import { faAngleRight } from '@fortawesome/pro-solid-svg-icons/faAngleRight';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faMagnifyingGlass';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { DateFormat, formatDateToString } from 'us-react-components/dist/utils/date';
import { SavedSearch } from 'us-web-services';
import { Routes } from '../../../../../Routes';
import { Button, ButtonType } from '../../../../Button';
import { SavedSearchWidgetCard } from './SavedSearchWidgetCard';

type Props = {
  parentSavedSearches: SavedSearch[];
}

export const SavedSearchWidget = (props: Props): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { parentSavedSearches } = props;
  const [filterCount, setFilterCount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [titleSavedSearch, setTitleSavedSearch] = useState<string>('');
  const [dateString, setDateString] = useState<string>('');

  const handleSavedSearch = (): void => {
    if (parentSavedSearches.length > 1) {
      navigate({
        pathname: generatePath(Routes.savedSearches)
      });
    } else {
      navigate({
        pathname: generatePath(Routes.parent.sitters),
        search: `${createSearchParams({
          searchId: (parentSavedSearches[0]?.id || 0).toString(),
        })}`,
      });
    }
  };

  const getNumberReceivingCare = (numberOfChildren: number, verticalTypeId: number): string => {
    if (numberOfChildren < 1) {
      return '';
    }
    const num = numberOfChildren >= 4 ? `${t('dashboard.widgets.savedSearch.fourPlus')}` : numberOfChildren.toString();
    switch (verticalTypeId) {
      case bookingVerticalType.CHILD_CARE:
        return `${num} ${numberOfChildren === 1 ? t('dashboard.widgets.savedSearch.child') : t('dashboard.widgets.savedSearch.children')}`;
      case bookingVerticalType.PET_CARE:
        return `${num} ${numberOfChildren === 1 ? t('dashboard.widgets.savedSearch.pet') : t('dashboard.widgets.savedSearch.pets')}`;
      case bookingVerticalType.SENIOR_CARE:
        return `${num} ${numberOfChildren === 1 ? t('dashboard.widgets.savedSearch.senior') : t('dashboard.widgets.savedSearch.seniors')}`;
      case bookingVerticalType.HOUSEHOLD_SERVICES:
      default:
        return '';
    }
  };

  const getWorksWithLabel = (savedSearch: SavedSearch, anyLabel: boolean = true): string => {
    let experienceLabel = '';

    if (savedSearch.ageExperience && savedSearch.ageExperience.length > 0) {
      for (let i = 0; i < savedSearch.ageExperience.length; i += 1) {
        const experience = savedSearch.ageExperience[i];
        if (experience?.label) {
          experienceLabel += `, ${experience.label.toLowerCase()}`;
        }
      }
    }

    if (savedSearch.willSitDog) {
      experienceLabel += `${t('dashboard.widgets.savedSearch.dogs')}`;
    }

    if (savedSearch.willSitCat) {
      experienceLabel += `${t('dashboard.widgets.savedSearch.cats')}`;
    }

    if (savedSearch.willSitOtherPet) {
      experienceLabel += `${t('dashboard.widgets.savedSearch.otherPets')}`;
    }

    if (savedSearch.willSitHome) {
      experienceLabel += `${t('dashboard.widgets.savedSearch.householdServices')}`;
    }

    return experienceLabel || (anyLabel ? `${t('dashboard.widgets.savedSearch.any')}` : '');
  };

  const lastAccess = {
    1: {
      label: `${t('dashboard.widgets.savedSearch.today')}`,
      value: 1,
    },
    7: {
      label: `${t('dashboard.widgets.savedSearch.pastWeek')}`,
      value: 7,
    },
    30: {
      label: `${t('dashboard.widgets.savedSearch.pastMonth')}`,
      value: 30,
    },
    90: {
      label: `${t('dashboard.widgets.savedSearch.past3Months')}`,
      value: 90,
    },
  };

  const buildDescription = (): void => {
    const lastSavedSearch: SavedSearch | undefined = parentSavedSearches[0];

    if (!lastSavedSearch) return;

    const { type, recurring, address, numberOfChildren, maximumRate, maxLastAccessDays, specialExperience, languages } = lastSavedSearch;

    const descriptionParts: string[] = [];

    if (type) {
      descriptionParts.push(`${type.label}`);
    }

    if (recurring?.length) {
      descriptionParts.push(`${t('dashboard.widgets.savedSearch.available')}`);
    }

    if (address) {
      const location = `${address.city}, ${address.state}`;
      const distance = `${lastSavedSearch.distance} ${t('dashboard.widgets.savedSearch.distance')}${lastSavedSearch.distance === 1 ? '' : t('dashboard.widgets.savedSearch.plural')}`;
      descriptionParts.push(`${location}, ${distance}`);
    }

    if (numberOfChildren) {
      const verticalTypeId = lastSavedSearch.verticalTypeId ?? 0;
      const numberReceivingCare = getNumberReceivingCare(numberOfChildren, verticalTypeId);
      if (numberReceivingCare) {
        descriptionParts.push(numberReceivingCare);
      }
    }

    if (maximumRate) {
      descriptionParts.push(`${t('dashboard.widgets.savedSearch.under')} ${t('dashboard.widgets.savedSearch.symbol')}${maximumRate}${t('dashboard.widgets.savedSearch.time')}`);
    }

    const worksWith = getWorksWithLabel(lastSavedSearch, false);
    if (worksWith) {
      descriptionParts.push(worksWith);
    }

    if (maxLastAccessDays !== undefined && maxLastAccessDays in lastAccess) {
      const key = maxLastAccessDays as keyof typeof lastAccess;
      descriptionParts.push(`${t('dashboard.widgets.savedSearch.active')} ${lastAccess[key].label.toLowerCase()}`);
    }

    const singleFilterMap = {
      acceptsCreditCard: `${t('dashboard.widgets.savedSearch.acceptsCreditCard')}`,
      willingToDriveChildren: `${t('dashboard.widgets.savedSearch.willingToDriveKinds')}`,
      hasCar: `${t('dashboard.widgets.savedSearch.hasACar')}`,
      respondsQuickly: `${t('dashboard.widgets.savedSearch.respondsQuickly')}`,
      backgroundCheck: `${t('dashboard.widgets.savedSearch.hasdoneBackgroundCheck')}`,
    };

    Object.keys(singleFilterMap).forEach((key) => {
      const typedKey = key as keyof typeof singleFilterMap;
      if (lastSavedSearch[typedKey]) {
        descriptionParts.push(singleFilterMap[typedKey]);
      }
    });

    if (specialExperience?.length) {
      const experienceLabels = specialExperience.map(exp => exp.label).join(', ');
      descriptionParts.push(`${t('dashboard.widgets.savedSearch.hasExperienceWith')} ${experienceLabels}`);
    }

    if (languages?.length) {
      const languageLabels = languages.map(lang => lang.label).join(', ');
      descriptionParts.push(`${t('dashboard.widgets.savedSearch.speaks')} ${languageLabels}`);
    }

    setDescription(descriptionParts.join(', ').replace(/,\s*$/, ''));
    setFilterCount(`${(lastSavedSearch.filterCount ?? 0).toString()} ${t('dashboard.widgets.savedSearch.filters')}`);
    setTitleSavedSearch(lastSavedSearch.title ?? '');
    if (lastSavedSearch.modified) {
      setDateString(formatDateToString(lastSavedSearch.modified, DateFormat.DATE_FULL_FORMAT));
    } else {
      setDateString('');
    }
  };

  useEffect(() => {
    buildDescription();
  }, []);

  return (
    <SavedSearchWidgetCard
      iconName={faMagnifyingGlass}
      iconClasses='text-tansy'
      title={ parentSavedSearches.length > 1 ? t('dashboard.widgets.savedSearch.titlePlural') : t('dashboard.widgets.savedSearch.title') }
      tittleSavedSearch={titleSavedSearch}
      dateString={dateString}
      filterCount={filterCount}
      description={description}
      hasButton
      buttonComponent={<Button
          buttonType={ButtonType.secondary}
          onClick={handleSavedSearch}>{ parentSavedSearches.length > 1 ? t('dashboard.widgets.savedSearch.viewSavedSearches') : t('dashboard.widgets.savedSearch.viewSavedSearch') }
          <FontAwesomeIcon icon={faAngleRight}  className='pl-2 m-[-1px]' />
        </Button>
      }
    />
  );
};
